import React from "react";
import { useQuery, useAction } from "wasp/client/operations";
import {
  getCommunityItemById,
  deleteCommunityPost,
} from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { useNavigate, useParams } from "react-router-dom";
import { DashboardLayout } from "../../../client/components/Sidebar";
import { useLocationContext } from "../../../client/contexts/LocationContext";
import {
  IndividualViewPageLayout,
  SidebarSection,
  ActionButtons,
  DetailField,
  ActionButtonLink,
  ActionButtonDanger,
} from "../../../components/layout";
import { Phone, Mail, Calendar, Edit, Trash2, AlertCircle } from "lucide-react";
import { SubscribeButton } from "../../content/components/SubscribeButton";
import type { CommunitySectionConfig } from "../types";
import { getCommunityBreadcrumbDropdownItems } from "../constants";

function getListPath(
  section: CommunitySectionConfig,
  locationCode?: string | null,
) {
  const base = `/${section.path}`;
  return locationCode ? `/locations/${locationCode}${base}` : base;
}

export interface CommunityViewPageProps {
  section: CommunitySectionConfig;
}

export default function CommunityViewPage({ section }: CommunityViewPageProps) {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const { itemId } = useParams<{ itemId: string }>();
  const deleteFn = useAction(deleteCommunityPost);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const {
    data: item,
    isLoading,
    error,
  } = useQuery(getCommunityItemById, {
    itemId: itemId!,
    contentType: section.contentType,
  });

  const listPath = getListPath(section, selectedLocation?.locationCode);

  const handleGoBack = () => navigate(listPath);

  const handleDelete = async () => {
    if (
      !window.confirm(
        "Are you sure you want to delete this post? This cannot be undone.",
      )
    )
      return;
    setIsDeleting(true);
    try {
      await deleteFn({ itemId: itemId! });
      navigate(listPath);
    } catch {
      alert("Failed to delete. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  if (!user) {
    return (
      <DashboardLayout>
        <div className="p-6 text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Access Denied
          </h3>
          <p className="text-gray-600">
            You must be logged in to view this post.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !item) {
    return (
      <DashboardLayout>
        <div className="p-6 text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Post Not Found
          </h3>
          <p className="text-gray-600 mb-4">
            The post you're looking for doesn't exist.
          </p>
          <button
            onClick={handleGoBack}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const isAdmin = user?.isAdmin;
  const meta = (item.metadata || {}) as {
    contactInfo?: string;
    contactPhone?: string;
    contactEmail?: string;
  };
  const currentPageUrl =
    typeof window !== "undefined" ? window.location.href : "";

  const breadcrumbItems = [
    {
      label: "Community",
      dropdownItems: getCommunityBreadcrumbDropdownItems(
        selectedLocation?.locationCode ?? null,
      ),
    },
    { label: section.label, isClickable: true, href: listPath },
  ];

  const sidebarContent = (
    <>
      {isAdmin && (
        <SidebarSection isFirst>
          <ActionButtons>
            <ActionButtonLink to={`${listPath}/${item.id}/edit`} icon={Edit}>
              Edit Post
            </ActionButtonLink>
            <ActionButtonDanger
              onClick={handleDelete}
              disabled={isDeleting}
              icon={Trash2}
            >
              {isDeleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline-block mr-2" />
                  Deleting...
                </>
              ) : (
                "Delete Post"
              )}
            </ActionButtonDanger>
          </ActionButtons>
        </SidebarSection>
      )}
      <SidebarSection title="Details">
        <div className="space-y-4">
          <DetailField
            label="Posted by"
            value={item.author?.username || item.author?.email || "N/A"}
          />
          <DetailField
            label="Posted on"
            value={new Date(item.createdAt).toLocaleDateString()}
          />
          {item.expiryDate && (
            <DetailField
              label="Expires"
              value={new Date(item.expiryDate).toLocaleString()}
            />
          )}
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualViewPageLayout
      breadcrumbItems={breadcrumbItems}
      currentPageUrl={currentPageUrl}
      sidebarContent={sidebarContent}
    >
      <div className="mb-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center justify-between gap-4">
              <h2 className="text-2xl font-bold text-content-text">
                {item.title}
              </h2>
              <SubscribeButton contentId={item.id} />
            </div>
            <p className="text-sm text-gray-500 mt-1">{section.label}</p>
          </div>
        </div>
        <p className="text-content-text text-lg leading-relaxed">
          {item.description || "—"}
        </p>
      </div>

      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <DetailField
              label="Author"
              value={item.author?.username || item.author?.email || "N/A"}
            />
            <DetailField
              label="Date"
              value={new Date(item.createdAt).toLocaleString()}
            />
          </div>
          <div className="space-y-4">
            {(meta.contactPhone || meta.contactEmail || meta.contactInfo) && (
              <>
                {meta.contactPhone && (
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <div className="text-sm font-medium text-gray-500">
                        Phone
                      </div>
                      <div className="text-gray-900">{meta.contactPhone}</div>
                    </div>
                  </div>
                )}
                {meta.contactEmail && (
                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <div className="text-sm font-medium text-gray-500">
                        Email
                      </div>
                      <div className="text-gray-900">{meta.contactEmail}</div>
                    </div>
                  </div>
                )}
                {!meta.contactPhone &&
                  !meta.contactEmail &&
                  meta.contactInfo && (
                    <div className="flex items-start gap-3">
                      <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <div className="text-sm font-medium text-gray-500">
                          Contact
                        </div>
                        <div className="text-gray-900">{meta.contactInfo}</div>
                      </div>
                    </div>
                  )}
              </>
            )}
            {item.expiryDate && (
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <div className="text-sm font-medium text-gray-500">
                    Expires
                  </div>
                  <div className="text-gray-900">
                    {new Date(item.expiryDate).toLocaleString()}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </IndividualViewPageLayout>
  );
}
